import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { User } from './user.model';
import { Injectable } from '@angular/core';
import { environment } from '../../../../../../environments/environment';
import { AuthService } from '../../../../../core/services/auth.service';
import { ImpersonateModel } from '../models/impersonate.model';
import { KeycloakLoginResponse } from '../../../../../shared/objects/auth/keycloakLoginResponse';
import { OAuthService, UserInfo } from 'angular-oauth2-oidc';

@Injectable()
export class UserService {
  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private oauthService: OAuthService,
  ) {}

  getUsers(): Observable<User[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<User[]>(`${environment.userServiceUrl}/getAll`, {
      headers,
    });
  }

  impersonate(username: string): Observable<KeycloakLoginResponse> {
    const headers = this.authService.getBearerRequestHeaders();
    const impersonateModel: ImpersonateModel = {
      username: username,
    };

    return this.httpClient.post<KeycloakLoginResponse>(
      `${environment.userServiceUrl}/impersonate`,
      impersonateModel,
      { headers },
    );
  }

  impersonateUser(username: string): Promise<UserInfo> {
    return this.impersonate(username)
      .toPromise()
      .then(token => {
        this.oauthService.logOut();
        localStorage.setItem('refresh_token', token.access_token);
        localStorage.setItem('access_token', token.access_token);
        sessionStorage.setItem('imp', token.impersonated?.toString());
        return this.oauthService
          .loadUserProfile()
          .then(userInfo => userInfo['info'] as UserInfo);
      });
  }

  getUsersForCarrier(): Observable<User[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<User[]>(
      `${environment.userServiceUrl}/getAllForCarrier`,
      { headers },
    );
  }

  lockUnlockUser(user: User): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.put<boolean>(
      `${environment.userServiceUrl}/lockUnlockUser/${user.id}`,
      user,
      { headers },
    );
  }

  getSingleUser(userId: number): Observable<User> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<User>(
      `${environment.userServiceUrl}/getSingleUser/${userId}`,
      { headers },
    );
  }

  getSingleUserByUsername(): Observable<User> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<User>(
      `${environment.userServiceUrl}/getSingleUserByUsername`,
      { headers },
    );
  }

  add(user: User) {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.post<User>(
      `${environment.userServiceUrl}/addUser`,
      user,
      { headers },
    );
  }

  edit(user: User) {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.put<User>(
      `${environment.userServiceUrl}/editUser/${user.id}`,
      user,
      { headers },
    );
  }

  getUserModules(userId: number) {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.put<User>(
      `${environment.userServiceUrl}/getUserModules/${userId}`,
      { headers },
    );
  }

  deleteUser(userId: number): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.delete<boolean>(
      `${environment.userServiceUrl}/deleteUser/${userId}`,
      { headers },
    );
  }

  updatePassword(user: User, newPassword: string): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.put<boolean>(
      `${environment.userServiceUrl}/updatePassword/${encodeURIComponent(newPassword)}`,
      user,
      { headers },
    );
  }

  resetPassword(usernameOrEmail: string): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeadersAnonymous();
    return this.httpClient.get<boolean>(
      `${environment.anonymousServiceUrl}/resetPassword/${usernameOrEmail}`,
      { headers },
    );
  }

  adminResetPassword(usernameOrEmail: string): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeadersAnonymous();
    return this.httpClient.get<boolean>(
      `${environment.anonymousServiceUrl}/adminResetPassword/${usernameOrEmail}`,
      { headers },
    );
  }

  elevateUserToAdmin(userId: number): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.delete<boolean>(
      `${environment.userServiceUrl}/elevateUser/${userId}`,
      { headers },
    );
  }

  validateResetPasswordGuid(guid: string): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeadersAnonymous();
    return this.httpClient.get<boolean>(
      `${environment.anonymousServiceUrl}/validateResetPasswordGuid/${guid}`,
      { headers },
    );
  }

  getUserByResetPasswordGuid(guid: string): Observable<User> {
    const headers = this.authService.getBearerRequestHeadersAnonymous();
    return this.httpClient.get<User>(
      `${environment.anonymousServiceUrl}/userByResetPasswordGuid/${guid}`,
      { headers },
    );
  }

  updatePasswordAnonymous(
    guid: string,
    newPassword: string,
  ): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeadersAnonymous();
    return this.httpClient.get<boolean>(
      `${
        environment.anonymousServiceUrl
      }/updatePassword/${guid}/${encodeURIComponent(newPassword)}`,
      { headers },
    );
  }
}
